/**
 * Product Card Variant Image Switcher
 * Handles switching between variant images when variant dots are clicked
 * Supports multiple instances per page with proper isolation
 */

// Prevent multiple initializations
if (typeof ProductCardImageSwitcher === "undefined") {
  class ProductCardImageSwitcher {
    constructor(cardId) {
      this.cardId = cardId;
      this.dotsContainer = null;
      this.imageContainer = null;
      this.initialized = false;

      // Initialize the instance
      this.init();
    }

    /**
     * Initialize the product card instance
     */
    init() {
      // Find the specific dots container and image container for this card
      this.dotsContainer = document.querySelector(`[data-variant-dots="${this.cardId}"]`);
      this.imageContainer = document.querySelector(`[data-product-card-images="${this.cardId}"]`);

      if (!this.dotsContainer || !this.imageContainer) {
        console.warn(`Product card components not found for card: ${this.cardId}`);
        return;
      }

      const dots = this.dotsContainer.querySelectorAll('[data-variant-dot]');
      
      if (dots.length === 0) {
        return;
      }

      this.initialized = true;

      // Set initial active state for first variant
      this.setActiveDot(dots[0]);

      // Add click handlers to all dots
      dots.forEach(dot => {
        dot.addEventListener('click', this.handleDotClick.bind(this, dots, dot));
      });
    }

    /**
     * Handle variant dot click events
     * @param {NodeList} allDots - All variant dots
     * @param {Element} clickedDot - The clicked dot element
     * @param {Event} event - The click event
     */
    handleDotClick(allDots, clickedDot, event) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = clickedDot.dataset.variantDot;
      
      if (!variantId) return;

      this.switchImage(variantId);
      this.updateActiveDot(allDots, clickedDot);
    }

    /**
     * Switch to the specified variant image
     * @param {string} targetVariantId - ID of the target variant
     */
    switchImage(targetVariantId) {
      if (!this.initialized) return;

      const images = this.imageContainer.querySelectorAll('[data-variant-image]');
      const targetImage = this.imageContainer.querySelector(`[data-variant-image="${targetVariantId}"]`);

      if (!targetImage) {
        console.warn(`Product card: Target image not found for variant: ${targetVariantId}`);
        return;
      }

      // Hide all images
      images.forEach(img => {
        img.style.opacity = '0';
      });

      // Show target image with slight delay for smooth transition
      requestAnimationFrame(() => {
        targetImage.style.opacity = '1';
      });
    }

    /**
     * Update active state for variant dots
     * @param {NodeList} allDots - All variant dots
     * @param {Element} activeDot - The dot to set as active
     */
    updateActiveDot(allDots, activeDot) {
      allDots.forEach(dot => {
        const isActive = dot === activeDot;
        
        // Update visual state
        dot.classList.toggle('scale-105', isActive);
        dot.classList.toggle('inset-ring-4', isActive);
        dot.classList.toggle('bg-white', !isActive);
        dot.classList.toggle('bg-green', isActive);
        
        // Update accessibility state
        dot.setAttribute('aria-pressed', isActive.toString());
      });
    }   

    /**
     * Set a dot as active (used for initialization)
     * @param {Element} dot - The dot element to set as active
     */
    setActiveDot(dot) {
      dot.classList.add('scale-105', 'bg-green');
      dot.classList.remove('bg-white');
      dot.setAttribute('aria-pressed', 'true');
    }

    /**
     * Cleanup and destroy instance
     */
    destroy() {
      // Remove event listeners would go here if we stored references
      this.initialized = false;
    }
  }

  // Expose to global scope
  window.ProductCardImageSwitcher = ProductCardImageSwitcher;

  /**
   * Product Card Manager
   * Manages multiple ProductCardImageSwitcher instances across the page
   */
  class ProductCardManager {
    constructor() {
      this.instances = new Map();
      this.initialized = false;
    }

    /**
     * Initialize all product cards found on the page
     */
    initializeAll() {
      if (this.initialized) {
        return;
      }

      // Find all product cards by looking for variant dots containers
      const variantDotsContainers = document.querySelectorAll('[data-variant-dots]');

      variantDotsContainers.forEach((dotsContainer) => {
        const cardId = dotsContainer.dataset.variantDots;
        
        if (!cardId) {
          console.warn("Product card found without proper card ID");
          return;
        }

        // Create instance if not already exists
        if (!this.instances.has(cardId)) {
          const instance = new ProductCardImageSwitcher(cardId);
          this.instances.set(cardId, instance);
        }
      });

      this.initialized = true;
    }

    /**
     * Add new instance (for dynamically added cards)
     */
    addInstance(cardId) {
      if (!this.instances.has(cardId)) {
        const instance = new ProductCardImageSwitcher(cardId);
        this.instances.set(cardId, instance);
        return instance;
      }
      return this.instances.get(cardId);
    }

    /**
     * Remove instance (for dynamically removed cards)
     */
    removeInstance(cardId) {
      const instance = this.instances.get(cardId);
      if (instance) {
        instance.destroy();
        this.instances.delete(cardId);
      }
    }
  }

  // Create global manager instance
  window.ProductCardManager = ProductCardManager;
  window.productCardManager = new ProductCardManager();

  /**
   * Initialize when DOM is ready
   */
  function initializeProductCards() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        window.productCardManager.initializeAll();
      });
    } else {
      // DOM already loaded
      window.productCardManager.initializeAll();
    }
  }

  /**
   * Handle dynamic content changes (theme editor, AJAX, etc.)
   */
  function setupDynamicHandlers() {
    // Shopify theme editor events
    // NOTE: event.target.id gives the full DOM ID (e.g. "shopify-section-...")
    // event.detail.sectionId omits the "shopify-section-" prefix.
    document.addEventListener("shopify:section:load", (event) => {
      if (!event.target) return;
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        // Re-initialize all cards in case new ones were added
        window.productCardManager.initialized = false;
        window.productCardManager.initializeAll();
      }, 100);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const sectionElement = event.target;
      if (sectionElement) {
        // Find and remove instances that belonged to this section
        const cardContainers = sectionElement.querySelectorAll('[data-variant-dots]');
        cardContainers.forEach(container => {
          const cardId = container.dataset.variantDots;
          if (cardId) {
            window.productCardManager.removeInstance(cardId);
          }
        });
      }
    });
  }

  // Initialize everything
  initializeProductCards();
  setupDynamicHandlers();
} // End initialization check
